import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, Output, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormFile } from 'src/core/models/form-file';
import { ModalConfirmComponent } from '../confirm-modal/confirm-modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DialogService } from 'src/core/services/DialogService';
import { ApiResponse } from 'src/core/models/api-response.model';


@Component({
    selector: 'detail-view',
    templateUrl: 'detail-view.html',
    styleUrls: ['detail-view.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DetailViewComponent implements AfterViewInit {
    @Input() apiUrl: string;
    @Input() url: string;
    @Input() title: string;
    @Input() httpMethod = 'auto';
    @Input() showButtons = true;
    @Input() showDelete = true;
    @Input() showSubmit = true;
    @Input() showGoBackButton = true;
    @Input() confirmOnSave = false;
    @Input() forceLoadingDataOnStart;
    @Input() formGroup: UntypedFormGroup;
    @Input() formFiles: FormFile[];
    @Input() afterAddNavigationAction: 'list' | 'edit' = 'edit';
    @Output() submitting = new EventEmitter<{ data, cancelled, done }>();
    @Output() submitted = new EventEmitter<{ data }>();
    @Output() dataLoaded = new EventEmitter<{ data }>();
    id: number;
    loading = false;
    deleting;

    constructor(private http: HttpClient,
        private router: Router,
        route: ActivatedRoute,
        private modal: NzModalService,
        private message: NzMessageService,
        private translate: TranslateService,
        private dialog: DialogService) {
        route.params.subscribe(params => {
            this.id = params.id;
        });
    }

    goBack() {
        let url = this.url || this.apiUrl;
        // url = url.substring(0, url.lastIndexOf('/'));
        this.router.navigate([url]);
    }

    private load() {
        this.loading = true;
        this.http.get<any>(`${this.apiUrl}${this.id ? `/${this.id}` : ''}`).subscribe(data => {
            // message
            const args = { data };
            this.dataLoaded.emit(args);
            this.formGroup.reset(args.data);
            this.loading = false;
        });
    }

    submit() {
        if (this.formGroup.invalid) {
            return;
        }
        if (this.formFiles && this.formFiles.filter(a => a.required && !a.file).length) {
            return;
        }

        const args: any = {
            data: this.formGroup.value,
            cancelled: false,
        };
        args.done = () => this.submitInternally(args);
        this.submitting.emit(args);
        if (args.cancelled) {
            return;
        }

        if (this.confirmOnSave) {
            this.dialog.confirm('common/save-confirm/title',
                'common/save-confirm/message', () => this.submitInternally(args)
            );
        }
        else {
            this.submitInternally(args);
        }
    }

    private submitInternally(args) {
        let data = args.data;

        if (this.formFiles && this.formFiles.length) {
            const formData = new FormData();
            formData.append('model', JSON.stringify(data));
            this.formFiles.forEach(f => {
                formData.append(f.name, f.file);
            });
            data = formData;
        }

        const request = this.httpMethod === 'auto' ? (this.id ? this.http.put : this.http.post) : this.http[this.httpMethod || 'post'];
        request
            .apply(this.http, [`${this.apiUrl}/${this.id || ''}`, data])
            .subscribe((result: ApiResponse<any>) => {
                if (result.succeeded) {
                    this.message.success(this.translate.instant('common/messages/submit-succeeded'));
                    this.submitted.emit({ data });

                    if (result.result) {
                        this.formGroup.reset(result.result);
                        if (this.afterAddNavigationAction === 'edit') {
                            if (!this.id) {
                                this.router.navigate([(this.url || this.apiUrl) + '/edit', result.result.id]);
                            }
                        }
                        else {
                            this.router.navigate([this.url || this.apiUrl]);
                        }
                    }
                    else {
                        this.formGroup.markAsPristine();
                        this.formGroup.reset();
                    }
                }
                else {
                    this.message.error(result.errorDescription || this.translate.instant('common/messages/submit-failed'));
                }
            },
                (error) => {
                    // Handle error response here
                    this.message.error(error.error?.errorDescription || this.translate.instant('common/messages/submit-failed'));
                });
    }

    delete() {
        const modal = this.modal.create({
            nzTitle: this.translate.instant('common/messages/delete/title'),
            nzContent: ModalConfirmComponent,
            // nzViewContainerRef: this.viewContainerRef,
            nzFooter: null,
            nzData: {}
        });
        modal.afterClose.subscribe(result => {
            if (result === 'yes') {
                this.deleting = true;
                this.http.delete(`${this.apiUrl}/${this.id}`).subscribe(data => {
                    this.message.success(this.translate.instant('common/messages/delete-succeeded'));
                    this.deleting = false;
                    this.router.navigate([(this.url || this.apiUrl)]);
                }, () => {
                    this.deleting = false;
                    this.message.error(this.translate.instant('common/messages/delete-failed'));
                });
            }
        });
    }

    ngAfterViewInit() {
        if (this.id || this.forceLoadingDataOnStart) {
            this.load();
        }
    }
}
